// @flow
import gql from 'graphql-tag';
import React, {Component} from 'react';
import classnames from 'classnames';
import Experiment from '@homeaway/react-experiment';
import {HA_PULSE_SAVE_CONFIRMATION_SERP_HIT} from '../../abacus/experiments';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import {
    SleepsElement,
    BedroomsPropertyTypeElement,
    BathroomsElement
} from '../Details/Details';
import {fragments as hitFragments} from '../Hit/Hit';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import {MAP_PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';
import {listingHasPrice} from '../Price/Price';
// $FlowFixMe
import TripBoardButton from '../TripBoardButton/TripBoardButton';
// $FlowFixMe
import TripBoardConfirmation from '../TripBoardButton/TripBoardConfirmation';
import './MapHit.less';

import type {HitEventDataListingShape} from '../../utils/hitEdapUtils';
import type {ListingShape as HitListingShape} from '../Hit/Hit';
import type {IntlShape} from 'react-intl';

export const fragments = {
    listing: gql`
        fragment MapHitListing on Listing {
            ...HitListing
            geoCode {
                latitude
                longitude
            }
        }
        ${hitFragments.listing}
    `
};

export type ListingShape = {
    geoCode?: {
        latitude: string,
        longitude: string
    }
} & HitListingShape;

type Props = {
    intl: IntlShape,
    listing: ListingShape,
    // TODO: fix onClick here.
    onHitClick?: (HitEventDataListingShape) => void,
    hitIndex?: number,
    page?: number,
    pageSize?: number,
    fullLocale: string
};

type State = {
    isLoading: boolean
};

/**
 * Typically rendered within a google maps infobox, which stops propagation on all
 * events. Sets up a ref on the main hit anchor element. No other DOM events
 * will work unless you create refs and wire them up manually.
 */
export default class MapHit extends Component<Props, State> {
    constructor() {
        super();
        this.state = {
            isLoading: true
        };
    }

    componentDidMount() {
        /* istanbul ignore else */
        if (this.linkRef.current) {
            this.linkRef.current.addEventListener('click', this.onClick);
        }
    }

    componentWillUnmount() {
        /* istanbul ignore else */
        if (this.linkRef.current) {
            this.linkRef.current.removeEventListener('click', this.onClick);
        }
    }

    // TODO: should be able to type this with something like `Ref<HTMLAnchorElement>`,
    // but Flow 0.74 complains. Try again when https://flow.org/en/docs/react/refs/
    // is updated to reflect new `React.createRef` API.
    linkRef: {current: null | HTMLAnchorElement} = React.createRef();

    onClick = () => {
        if (this.props.onHitClick) {
            this.props.onHitClick({
                listing: this.props.listing,
                hitIndex: this.props.hitIndex,
                page: this.props.page,
                pageSize: this.props.pageSize
            });
        }
    };

    render() {
        const {intl, listing, fullLocale} = this.props;
        const {
            bathrooms,
            bedrooms,
            detailPageUrl,
            instantBookable,
            listingId,
            propertyType,
            sleeps
        } = listing;

        const bathroomsHalf = bathrooms && bathrooms.half;
        const bathroomsFull = bathrooms && bathrooms.full;
        const bathroomsToiletOnly = bathrooms && bathrooms.toiletOnly;
        const hasPrice = listingHasPrice(listing);
        const mapHitRatingClassName = classnames({
            'MapHit__info--priceRating-review': hasPrice,
            'MapHit__info--priceRating-review-noPrice': !hasPrice
        });
        const listingImages = (listing && listing.images) || [];
        const listingImagesCount = (listing && listing.imageCount) || 0;
        return (
            <div className="MapHit" data-wdio="MapHit">
                <div
                    className="MapHit__carousel content--clickable"
                    data-wdio="MapHit__carousel"
                >
                    <div className="Hit__tripboardButton">
                        <TripBoardButton listing={listing} isMapHit />
                    </div>
                    <HitCarouselContainer
                        detailPageUrl={detailPageUrl}
                        edapAction={MAP_PHOTOCAROUSEL_ADVANCE}
                        images={listingImages}
                        listingId={listingId}
                        onImageClick={this.onClick}
                        totalImageCount={listingImagesCount}
                    />
                </div>
                <Experiment
                    name={HA_PULSE_SAVE_CONFIRMATION_SERP_HIT}
                    variant={'on'}
                    deferLogging
                >
                    <TripBoardConfirmation
                        actionLocation={'search_map'}
                        isMapHit
                        listing={listing}
                        size={'sm'}
                    />
                </Experiment>
                <a
                    className={'MapHit__link a--plain-link'}
                    href={listing.detailPageUrl}
                    ref={this.linkRef}
                    target={'_blank'}
                >
                    <div className="MapHit__info">
                        <div className="MapHit__info--details">
                            <BedroomsPropertyTypeElement
                                bedrooms={bedrooms}
                                propertyType={propertyType}
                            />
                            <BathroomsElement
                                bathroomsFull={bathroomsFull}
                                bathroomsHalf={bathroomsHalf}
                                bathroomsToiletOnly={bathroomsToiletOnly}
                            />
                            <SleepsElement value={sleeps} shouldHideDot />
                        </div>
                        <div className="MapHit__info--priceRating">
                            <div className="MapHit__info--priceRating-price">
                                <Price
                                    instantBookable={instantBookable}
                                    intl={intl}
                                    listing={listing}
                                    useAverageLabel
                                    useShortLabel
                                    fullLocale={fullLocale}
                                />
                            </div>
                            <div className={mapHitRatingClassName}>
                                <Rating
                                    listing={filterGraphQlAnywhere(
                                        ratingFragments.listing,
                                        listing
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </a>
            </div>
        );
    }
}
